import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Save,
  ArrowLeft,
  Loader2,
  Star,
  User,
  Package,
  MessageSquare,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  GetSingleReviewThunk,
  AddReviewThunk,
  UpdateReviewThunk,
  AllProdutListThunk,
  AllUserListThunk,
} from "../../redux/states/reviews/thunk";

const CreateUpdateReview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();

  const { singleReview, products, users, loading, error } = useSelector(
    (state) => state.reviews
  );

  const [formData, setFormData] = useState({
    product_id: "",
    user_id: "",
    rating: "",
    title: "",
    comment: "",
    status: "active",
  });

  const [localLoading, setLocalLoading] = useState(false);

  // ✅ Load single review if editing
  useEffect(() => {
    if (uuid) {
      setLocalLoading(true);
      dispatch(GetSingleReviewThunk(uuid)).finally(() =>
        setLocalLoading(false)
      );
    }
  }, [dispatch, uuid]);

  // ✅ Load all products and users
  useEffect(() => {
    dispatch(AllProdutListThunk());
    dispatch(AllUserListThunk());
  }, [dispatch]);

  // ✅ Populate form after data load
  useEffect(() => {
    if (singleReview && uuid) {
      setFormData({
        product_id: singleReview.product_id || "",
        user_id: singleReview.user_id || "",
        rating: singleReview.rating || "",
        title: singleReview.title || "",
        comment: singleReview.comment || "",
        status: singleReview.status || "active",
      });
    }
  }, [singleReview, uuid]);

  // ✅ Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Star rating handler
  const handleStarClick = (rating) => {
    setFormData((prev) => ({ ...prev, rating: rating.toString() }));
  };

  // ✅ Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalLoading(true);

    try {
      if (uuid) {
        await dispatch(UpdateReviewThunk({ uuid, formData })).unwrap();
        toast.success("Review updated successfully! 🎉");
      } else {
        await dispatch(AddReviewThunk(formData)).unwrap();
        toast.success("Review added successfully! 🎉");
      }
      navigate("/reviews");
    } catch (err) {
      toast.error(err || "Failed to save review");
    } finally {
      setLocalLoading(false);
    }
  };

  // Star Rating Component
  const StarRating = ({ rating, onRatingChange }) => {
    return (
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRatingChange(star)}
            className={`p-1 transition-all duration-200 transform hover:scale-110 ${
              star <= rating ? "text-yellow-400" : "text-gray-300"
            }`}
          >
            <Star
              className="w-8 h-8"
              fill={star <= rating ? "currentColor" : "none"}
            />
          </button>
        ))}
        <span className="ml-2 text-sm font-medium text-gray-600">
          {rating}/5
        </span>
      </div>
    );
  };

  if (localLoading && uuid) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">Loading review data...</p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-12xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {uuid ? "Update Review" : "Create New Review"}
              </h1>
              <p className="text-gray-600 mt-2">
                {uuid
                  ? "Update the review details below"
                  : "Share your experience by creating a new review"}
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-3 px-4 py-2.5 text-gray-600 hover:text-blue-600 transition-all duration-200 bg-white rounded-xl shadow-sm hover:shadow-md border border-gray-200 hover:border-blue-200"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Go Back</span>
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 animate-fade-in">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">!</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-red-600 font-medium">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="space-y-8 bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 hover:shadow-2xl transition-all duration-300"
          >
            {/* Basic Information Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">
                  Basic Information
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Product Dropdown */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Package className="w-4 h-4" />
                    Product
                  </label>
                  <select
                    name="product_id"
                    value={formData.product_id}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-gray-300"
                    required
                  >
                    <option value="">Select a product</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* User Dropdown */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <User className="w-4 h-4" />
                    User
                  </label>
                  <select
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-gray-300"
                    required
                  >
                    <option value="">Select a user</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Rating Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Star className="w-5 h-5 text-yellow-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Rating</h2>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  How would you rate this product?
                </label>
                <StarRating
                  rating={parseInt(formData.rating) || 0}
                  onRatingChange={handleStarClick}
                />
                <input
                  type="number"
                  name="rating"
                  min="1"
                  max="5"
                  value={formData.rating}
                  onChange={handleChange}
                  className="hidden"
                  required
                />
              </div>
            </div>

            {/* Review Content Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                <div className="p-2 bg-green-100 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">
                  Review Content
                </h2>
              </div>

              <div className="space-y-6">
                {/* Title */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Review Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Give your review a title..."
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-gray-300 placeholder-gray-400"
                  />
                </div>

                {/* Comment */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Detailed Comment
                  </label>
                  <textarea
                    name="comment"
                    value={formData.comment}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Share your detailed experience with this product..."
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-gray-300 placeholder-gray-400 resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Status Section */}
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Review Status
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      checked={formData.status === "active"}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span
                      className={`px-4 py-2.5 rounded-lg border transition-all duration-200 ${
                        formData.status === "active"
                          ? "bg-green-50 border-green-200 text-green-700"
                          : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      Active
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      checked={formData.status === "inactive"}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span
                      className={`px-4 py-2.5 rounded-lg border transition-all duration-200 ${
                        formData.status === "inactive"
                          ? "bg-red-50 border-red-200 text-red-700"
                          : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      Inactive
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-100">
              <button
                type="submit"
                disabled={localLoading}
                className="w-full flex justify-center items-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:transform-none disabled:hover:scale-100 shadow-lg hover:shadow-xl"
              >
                {localLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{uuid ? "Updating..." : "Creating..."}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>{uuid ? "Update Review" : "Create Review"}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </NonAuthLayout>
  );
};

export default CreateUpdateReview;
